import React, { useState, useEffect, useMemo } from 'react';
import axios from '../../api/axios';
import { defaultLanguage, languageOptions } from '../../constants';
import {
    contactDefaultDisplay, contactDefaultEntries
} from '../../languages/defaultEntries'
import useAuth from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { SaveButton } from '../buttons/Buttons';

const WEBSITE_URL = '/contact';

const buildContactSnapshot = (data = {}) => ({
    name: data.name ?? '',
    lastname: data.lastname ?? '',
    street: data.street ?? '',
    number: data.number ?? 1,
    zip: data.zip ?? 1,
    city: data.city ?? '',
    country: data.country ?? '',
    tel: data.tel ?? '',
    telDisplayed: Boolean(data.telDisplayed),
    mail: data.mail ?? '',
    postBus: data.postBus ?? '',
    btw: data.btw ?? '',
    languages: data.languages ?? '',
    tourismLicense: data.tourismLicense ?? '',
    fb: data.fb ?? '',
    ig: data.ig ?? '',
    ln: data.ln ?? '',
    yt: data.yt ?? '',
});

export default function ContactInfoForm({ showRequiredErrors = false, missingRequired = [], onSaved }) {
    const art = defaultLanguage;
    const title = "contact";
    const { username, } = useAuth();
    const { t } = useTranslation();

    const defaultEntries = contactDefaultEntries;
    const defaultDisplay = contactDefaultDisplay;

    // const [loading, setLoading] = useState(true);
    const [isContentEmpty, setIsContentEmpty] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [canSave, setCanSave] = useState(false);

    const [name, setName] = useState('');
    const [lastname, setLastname] = useState('');
    const [street, setStreet] = useState('');
    const [number, setNumber] = useState(1);
    const [zip, setZip] = useState(1);
    const [postBus, setPostBus] = useState('');
    const [city, setCity] = useState('');
    const [country, setCountry] = useState('');
    const [tel, setTel] = useState('');
    const [telDisplayed, setTelDisplayed] = useState(false);
    const [mail, setMail] = useState('');
    const [mailError, setMailError] = useState(true);
    const [btw, setBtw] = useState('');
    const [languages, setLanguages] = useState([]);
    const [selectedLanguageOptions, setSelectedLanguageOptions] = useState([]);
    const [tourismLicense, setTourismLicense] = useState('');
    const [fb, setFbLink] = useState('');
    const [ig, setIgLink] = useState('');
    const [ln, setLnLink] = useState('');
    const [yt, setYtLink] = useState('');
    
    const [initialFormData, setInitialFormData] = useState(null);

    useEffect(() => {
        const getContent = async () => {
            try {
                const response = await axios.get(`${WEBSITE_URL}`, {
                    params: { username, art, title },
                });
                if (response.data.entry) {
                    const initial = buildContactSnapshot(response.data.entry);

                    setName(initial.name);
                    setLastname(initial.lastname);
                    setStreet(initial.street);
                    setNumber(initial.number);
                    setZip(initial.zip);
                    setCity(initial.city);
                    setCountry(initial.country);
                    setTel(initial.tel);
                    setTelDisplayed(initial.telDisplayed);
                    setMail(initial.mail);
                    setPostBus(initial.postBus);
                    setBtw(initial.btw);
                    setLanguages(initial.languages);
                    setSelectedLanguageOptions(String(initial.languages).split(",").filter(Boolean));
                    setTourismLicense(initial.tourismLicense);
                    setFbLink(initial.fb);
                    setIgLink(initial.ig);
                    setLnLink(initial.ln);
                    setYtLink(initial.yt);

                    setInitialFormData(initial);
                }
                // setLoading(false);

            } catch (err) {
                console.log(err);
            }
        };
        getContent();
    }, [username, art, title]);

    const saveContent = async () => {
        try {
            await axios.put(WEBSITE_URL, {
                username,
                art,
                title,
                name,
                lastname,
                street,
                number,
                zip,
                city,
                country,
                tel,
                telDisplayed,
                mail,
                postBus,
                btw,
                languages,
                tourismLicense,
                fb,
                ig,
                ln,
                yt
            });
            setIsSubmitted(true);
            setIsContentEmpty(false);
            if (typeof onSaved === "function") onSaved();
        } catch (err) {
            console.log(err);
        }
    };

    const isMissing = (key) => {
        return showRequiredErrors && Array.isArray(missingRequired) && missingRequired.includes(key);
    };

    const handleCheckboxChange = (option) => {
        setSelectedLanguageOptions((prevSelectedLanguageOptions) => {
            if (prevSelectedLanguageOptions.includes(option)) {
                return prevSelectedLanguageOptions.filter((selectedOption) => selectedOption !== option);
            } else {
                return [...prevSelectedLanguageOptions, option];
            }
        });
    };

    useEffect(() => {
        setLanguages(selectedLanguageOptions.join(","))
    }, [selectedLanguageOptions])

    const handleSaveClick = (event) => {
        event.preventDefault();
        if (isContentEmpty) {
            alert('Error: Text cannot be empty!');
            return;
        }
        // Pass the updated reviews array to saveContent function
        saveContent();
    };

    const handleMailChange = (event) => {
        setMail(event.target.value);
    };

    useEffect(() => {
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail) && mail !== "") {
            setMailError(false)
        } else {
            setMailError(true)
        }
    }, [mail, mailError])

    const currentFormData = useMemo(() => (
        buildContactSnapshot({
            name,
            lastname,
            street,
            number,
            zip,
            city,
            country,
            tel,
            telDisplayed,
            mail,
            postBus,
            btw,
            languages,
            tourismLicense,
            fb,
            ig,
            ln,
            yt,
        })
    ), [
        name,
        lastname,
        street,
        number,
        zip,
        city,
        country,
        tel,
        telDisplayed,
        mail,
        postBus,
        btw,
        languages,
        tourismLicense,
        fb,
        ig,
        ln,
        yt,
    ]);

    useEffect(() => {
        if (!initialFormData) {
            setCanSave(false);
            return;
        }

        const hasRequiredValues = Boolean(
            !mailError &&
            String(name || "").trim() &&
            String(lastname || "").trim() &&
            String(street || "").trim() &&
            Number(number) > 0 &&
            String(city || "").trim() &&
            Number(zip) > 0 &&
            String(country || "").trim() &&
            String(languages || "").trim()
        );
        const hasChanged = JSON.stringify(currentFormData) !== JSON.stringify(initialFormData);

        setCanSave(hasRequiredValues && hasChanged);
    }, [mailError, name, lastname, street, number, city, zip, country, languages, currentFormData, initialFormData])

    return (
        <div>
            <form className="review-form">
                <h3> {t("dashboard_page.contact_form.title")}</h3>
                <div className="required-fields-section" style={{ marginBottom: '16px' }}>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#dc3545', fontWeight: 500 }}>
                        <span className="required-asterisk" style={{ marginLeft: 0, marginRight: 6 }}>*</span>
                        Indicates required field
                    </p>
                </div>
                <div className="form-row">
                    <label className="form-label" htmlFor="name">
                        {t("dashboard_page.contact_form.firstname")}
                        <span className="required-asterisk">*</span>
                    </label>
                    <input
                        type="text"
                        id="name"
                        value={name === defaultEntries.name ? defaultDisplay.name : name}
                        placeholder={name === defaultEntries.name ? defaultDisplay.name : name}
                        onChange={(event) => setName(event.target.value)}
                        className={isMissing("name") || !name ? "invalid" : "valid"}
                    />
                </div>
                <div className="form-row">
                    <label className="form-label" htmlFor="lastname">
                        {t("dashboard_page.contact_form.lastname")}
                        <span className="required-asterisk">*</span>
                    </label>
                    <input
                        type="text"
                        id="lastname"
                        value={lastname === defaultEntries.lastname ? defaultDisplay.lastname : lastname}
                        placeholder={lastname === defaultEntries.lastname ? defaultDisplay.lastname : lastname}
                        className={isMissing("lastname") || !lastname ? "invalid" : "valid"}
                        onChange={(event) => setLastname(event.target.value)}
                    />
                </div>
                <div className="form-row">
                    <label className="form-label" htmlFor="street">
                        {t("dashboard_page.contact_form.street")}
                        <span className="required-asterisk">*</span>
                    </label>
                    <input
                        type="text"
                        id="street"
                        value={street === defaultEntries.street ? defaultDisplay.street : street}
                        placeholder={street === defaultEntries.street ? defaultDisplay.street : street}
                        className={isMissing("street") || !street ? "invalid" : "valid"}
                        onChange={(event) => setStreet(event.target.value)}
                    />
                </div>
                <div className="form-row">
                    <label className="form-label" htmlFor="number">
                        {t("dashboard_page.contact_form.number")}
                        <span className="required-asterisk">*</span>
                    </label>
                    <input
                        type="number"
                        id="number"
                        value={number === defaultEntries.number ? defaultDisplay.number : number}
                        placeholder={number === defaultEntries.number ? defaultDisplay.number : number}
                        className={isMissing("number") || !number ? "invalid" : "valid"}
                        onChange={(event) => setNumber(event.target.value)}
                    />
                </div>
                <div className="form-row">
                    <label className="form-label" htmlFor="Postbus">Postbus: </label>
                    <input
                        type="text"
                        id="postBus"
                        value={postBus === defaultEntries.postBus ? defaultDisplay.postBus : postBus}
                        placeholder={postBus === defaultEntries.postBus ? defaultDisplay.postBus : postBus}
                        onChange={(event) => setPostBus(event.target.value)}
                    />
                </div>
                <div className="form-row">
                    <label className="form-label" htmlFor="city">
                        {t("dashboard_page.contact_form.city")}
                        <span className="required-asterisk">*</span>
                    </label>
                    <input
                        type="text"
                        id="city"
                        value={city === defaultEntries.city ? defaultDisplay.city : city}
                        placeholder={city === defaultEntries.city ? defaultDisplay.city : city}
                        className={isMissing("city") || !city ? "invalid" : "valid"}
                        onChange={(event) => setCity(event.target.value)}
                    />
                </div>
                <div className="form-row">
                    <label className="form-label" htmlFor="zip">
                        {t("dashboard_page.contact_form.zip")}
                        <span className="required-asterisk">*</span>
                    </label>
                    <input
                        type="number"
                        id="zip"
                        value={zip === defaultEntries.zip ? defaultDisplay.zip : zip}
                        placeholder={zip === defaultEntries.zip ? defaultDisplay.zip : zip}
                        className={isMissing("zip") || !zip ? "invalid" : "valid"}
                        onChange={(event) => setZip(event.target.value)}
                    />
                </div>
                <div className="form-row">
                    <label className="form-label" htmlFor="country">
                        {t("dashboard_page.contact_form.country")}
                        <span className="required-asterisk">*</span>
                    </label>
                    <input
                        type="text"
                        id="country"
                        value={country === defaultEntries.country ? defaultDisplay.country : country}
                        placeholder={country === defaultEntries.country ? defaultDisplay.country : country}
                        className={isMissing("country") || !country ? "invalid" : "valid"}
                        onChange={(event) => setCountry(event.target.value)}
                    />
                </div>
                <div className="form-row">
                    <label className="form-label" htmlFor="guests">{t("dashboard_page.contact_form.displayTel")}</label>
                    <input
                        type="checkbox"
                        id="telDisplayed"
                        checked={telDisplayed}
                        style={{ width: "5%", padding: "", borderRadius: "5px", border: "1px solid #ccc", backgroundColor: "#f5f5f5", color: "#333", marginRight: "2px" }}
                        onChange={(event) => setTelDisplayed(event.target.checked)}
                    />
                    {telDisplayed && (
                        <input
                            type="text"
                            id="tel"
                            value={tel === defaultEntries.tel ? defaultDisplay.tel : tel}
                            placeholder={tel === defaultEntries.tel ? defaultDisplay.tel : tel}
                            style={{ width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ccc", backgroundColor: "#f5f5f5", color: "#333" }}
                            onChange={(event) => setTel(event.target.value)}
                        />
                    )}
                </div>
                <div className="form-row">
                    <label className="form-label" htmlFor="mail">
                        {t("dashboard_page.contact_form.mail")}
                        <span className="required-asterisk">*</span>
                    </label>
                    <input
                        type="text"
                        id="mail"
                        value={mail === defaultEntries.mail ? defaultDisplay.mail : mail}
                        placeholder={mail === defaultEntries.mail ? defaultDisplay.mail : mail}
                        onChange={handleMailChange}
                        className={isMissing("mail") || mailError ? "invalid" : "valid"}
                    />
                </div>
                <div className="form-row">
                    <label className="form-label" htmlFor="btw">{t("contact_page.vat")} </label>
                    <input
                        type="text"
                        id="btw"
                        value={btw === defaultEntries.btw ? defaultDisplay.btw : btw}
                        placeholder={btw === defaultEntries.btw ? defaultDisplay.btw : btw}
                        className="valid"
                        onChange={(event) => setBtw(event.target.value)}
                    />
                </div>
                <div className="form-row">
                    <label className="form-label" htmlFor="tourismLicense">{t("contact_page.tourism")} </label>
                    <input
                        type="text"
                        id="tourismLicense"
                        value={tourismLicense === defaultEntries.tourismLicense ? defaultDisplay.tourismLicense : tourismLicense}
                        placeholder={tourismLicense === defaultEntries.tourismLicense ? defaultDisplay.tourismLicense : tourismLicense}
                        className="valid"
                        onChange={(event) => setTourismLicense(event.target.value)}
                    />
                </div>
                <div className="form-row">
                    <label className="form-label" htmlFor="fbLink">{t("contact_page.fblink")} </label>
                    <input
                        type="text"
                        id="fb"
                        value={fb === defaultEntries.fb ? defaultDisplay.fb : fb}
                        placeholder={fb === defaultEntries.fb ? defaultDisplay.fb : fb}
                        className="valid"
                        onChange={(event) => setFbLink(event.target.value)}
                    />
                </div>
                <div className="form-row">
                    <label className="form-label" htmlFor="igLink">{t("contact_page.instalink")} </label>
                    <input
                        type="text"
                        id="ig"
                        value={ig === defaultEntries.ig ? defaultDisplay.ig : ig}
                        placeholder={ig === defaultEntries.ig ? defaultDisplay.ig : ig}
                        className="valid"
                        onChange={(event) => setIgLink(event.target.value)}
                    />
                </div>
                <div className="form-row">
                    <label className="form-label" htmlFor="lnLink">{t("contact_page.linkdinlink")} </label>
                    <input
                        type="text"
                        id="ln"
                        value={ln === defaultEntries.ln ? defaultDisplay.ln : ln}
                        placeholder={ln === defaultEntries.ln ? defaultDisplay.ln : ln}
                        className="valid"
                        onChange={(event) => setLnLink(event.target.value)}
                    />
                </div>
                <div className="form-row">
                    <label className="form-label" htmlFor="ytLink">{t("contact_page.youtubelink")} </label>
                    <input
                        type="text"
                        id="yt"
                        value={yt === defaultEntries.yt ? defaultDisplay.yt : yt}
                        placeholder={yt === defaultEntries.yt ? defaultDisplay.yt : yt}
                        className="valid"
                        onChange={(event) => setYtLink(event.target.value)}
                    />
                </div>
                <div className='form-row' style={{ paddingTop: "5px" }}>
                    <label className="form-label" htmlFor="languages">
                        {t("dashboard_page.contact_form.languages")}
                        <span className="required-asterisk">*</span>
                    </label>
                    {languageOptions.map((option) => (
                        <div
                            style={{ fontSize: "16px" }}
                            key={option}>
                            <label>
                                <input
                                    type="checkbox"
                                    style={{ margin: "5px" }}
                                    checked={selectedLanguageOptions.includes(option)}
                                    onChange={() => handleCheckboxChange(option)}
                                />
                                {option}
                            </label>
                        </div>
                    ))}
                </div>
                <SaveButton
                    buttonText={t("button.save")}
                    onClick={handleSaveClick}
                    enabled={canSave}
                />
                {isSubmitted && <p>{t("dashboard_page.contact_form.successmsg")}</p>}
                {/* {!canSave && <p className='error-msg'>{t("errors.fieldRequired")}</p>}
                {mailError && <p className='error-msg'>{t("errors.email")}</p>}
                {(!name || !lastname) && <p className='error-msg'>{t("errors.name")}</p>}
                {(!street || !number || !city || !zip || !country) && <p className='error-msg'>{t("errors.address")}</p>}
                {(languages === null) && <p className='error-msg'>{t("errors.languages")}</p>} */}
            </form>
        </div>
    );
}
